
import Investor, { AccountCreateProps, CreateAliasIfPossibleProps, CreateAliasIfPossibleResponse, InvestorCreateProps, UpdateInvestorChannelIncognitoProps, UpdateInvestorLanguageProps } from '../shared/Investor';
import { UserAPI } from '../config';
import API from '@aws-amplify/api';
import { showError } from './ErrorLib';
import Project, { ProjectId } from '../shared/Project';
import { defaultPrivatePolicy, PropertyBag, PropertyBagProps } from '../shared/KYCPolicy';
import Investment, { InvestmentAccountProps, InvestmentAmountProps, InvestmentId, InvestmentKnowledgeTestProps, InvestmentStepProps, NewInvestmentProps } from '../shared/Investment';
import { Dashboard, InvestInfo, PublicProjectData } from '../shared/UI';
import { NOTIFICATIONS_ALIAS, setLanguage, setLocalItem } from './LocalStorageHooks';
import { FileResponse } from '../shared/File';
import { ContractLink, ContractLinkProps, ContractSignatueHookProps } from '../shared/Contract';
import Post, { IncognitoChannelsResult, InvestorPostAndChannelsResult, InvestorPostsAndChannelsResult } from '../shared/Post';
import { Auth } from 'aws-amplify';
import { UINotification } from '../shared/Notifications';


async function wrapper<T>(execute: () => Promise<T>): Promise<T | undefined> {
    try {
        const result = await execute()
        return result
    } catch (error) {
        showError(error)
    }
    return undefined;
}

export const getInvestorWithoutWrapper = async (): Promise<Investor | undefined> => {
    return await API.get(UserAPI, '/investor', {})
}

export const getInvestor = async (): Promise<Investor | undefined> => wrapper(async () => {
    const result = await API.get(UserAPI, '/investor', {})
    return result
})

export const getFeed = async (): Promise<InvestorPostsAndChannelsResult | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/post', {})
})

export const getPostById = async (id: string): Promise<InvestorPostAndChannelsResult | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/post/' + id, {})
})
export const getPostByChannelId = async (id: string): Promise<Post[] | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/channel/post/' + id, {})
})


export const createInvestor = async (body: InvestorCreateProps): Promise<Investor | undefined> => {
    try {
        const result: Investor = await API.post(UserAPI, '/investor', { body })
        const user = await Auth.currentAuthenticatedUser()
        const { language, fname, lname, phone } = body
        setLanguage(language)
        await Auth.updateUserAttributes(user, {
            phone_number: phone,
            given_name: fname,
            family_name: lname
        })
        return result
    } catch (error) {
        showError(error)
    }
    return undefined;
}
export const createInvestorAccount = async (body: AccountCreateProps): Promise<Investor | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/investor/account', { body })
})

export const upadteInvestorLanguage = async (body: UpdateInvestorLanguageProps): Promise<Investor | undefined> => {
    try {
        const result: Investor = await API.post(UserAPI, '/investor/language', { body })
        setLanguage(body.language)
        return result
    } catch (error) {
        showError(error)
    }
    return undefined;
}

export const updateInvestorChannels = async (body: UpdateInvestorChannelIncognitoProps): Promise<boolean | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/investor/channels', { body })
})

export const getIncognitoInvestorChannels = async (id: string): Promise<IncognitoChannelsResult | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/incognito/channels/' + id, {})
})

export const resetInvestor = async (): Promise<Investor | undefined> => {
    try {
        const result: Investor = await API.del(UserAPI, '/investor', {})
        window.location.reload()
        return result
    } catch (error) {
        showError(error)
    }
    return undefined;
}

export const getKYCPolicy = async (index: number): Promise<PropertyBag | undefined> => wrapper(async () => {
    //TODO Remove this
    return defaultPrivatePolicy
    return await API.get(UserAPI, '/kyc/' + index, {})
})

export const getProjects = async (): Promise<Project[]> => wrapper(async () => {
    return await API.get(UserAPI, '/projects/public', {})
})

export const getProjectById = async (id: ProjectId): Promise<Project | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/project/' + id, {})
})
export const getProjectDetailedById = async (id: ProjectId): Promise<Project | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/project/detailed/' + id, {})
})

export const updateInvestorKYCInformation = async (props: PropertyBagProps): Promise<Investor | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/investor/kyc', { body: { ...props } })
})

export const createInvestment = async (props: NewInvestmentProps): Promise<Investment | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/investment', { body: { ...props } })
})

export const updateInvestmentAmount = async (investmentId: InvestmentId, props: InvestmentAmountProps): Promise<Investment | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/investment/amount/' + investmentId, { body: { ...props } })
})

export const updateInvestmentAccount = async (investmentId: InvestmentId, props: InvestmentAccountProps): Promise<Investment | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/investment/account/' + investmentId, { body: { ...props } })
})

export const updateInvestmentKnowledgetest = async (investmentId: InvestmentId, props: InvestmentKnowledgeTestProps): Promise<Investment | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/investment/knowledgetest/' + investmentId, { body: { ...props } })
})

export const getContract = async (investmentId: InvestmentId, props: ContractLinkProps): Promise<ContractLink | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/investment/contract/' + investmentId, { body: { ...props } })
})

export const signContract = async (investmentId: InvestmentId, props: ContractSignatueHookProps): Promise<Investment | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/investment/sign/' + investmentId, { body: { ...props } })
})

export const updateInvestmentStep = async (investmentId: InvestmentId, props: InvestmentStepProps): Promise<Investment | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/investment/step/' + investmentId, { body: { ...props } })
})

export const getProjectInvestments = async (projectId: ProjectId): Promise<Investment[] | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/investment/project/' + projectId, {})
})

export const getInvestments = async (): Promise<Investment[] | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/investment/', {})
})

export const getInvestmentById = async (investmentId: InvestmentId): Promise<Investment | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/investment/' + investmentId, {})
})

export const getInvestInfoById = async (investmentId: InvestmentId): Promise<InvestInfo | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/investment/invest/' + investmentId, {})
})

export const getInvestorDashboard = async (): Promise<Dashboard | undefined> => wrapper(async () => {
    const result = await API.get(UserAPI, '/dashboard', {})
    if (result) {
        const unredNotifications = result.notifications.filter((item: UINotification) => !item.viewed)
        const valueToBeSet = unredNotifications.length === 0? undefined : unredNotifications.length 
        setLocalItem(NOTIFICATIONS_ALIAS, valueToBeSet)
    }
    return result
})

export const deleteInvestment = async (investmentId: InvestmentId): Promise<InvestInfo | undefined> => wrapper(async () => {
    return await API.del(UserAPI, '/investment/' + investmentId, {})
})

export const uploadFile = async (file: File): Promise<string | undefined> => wrapper(async () => {
    const item: FileResponse = await API.get(UserAPI, '/upload/private', {})
    await fetch(item.uploadUrl, {
        method: 'PUT',
        body: file
    })
    return item.key
})

export const checkForAlias = async (body: CreateAliasIfPossibleProps): Promise<CreateAliasIfPossibleResponse> => wrapper(async () => {
    return await API.post(UserAPI, '/investor/alias/', { body })
})


export const sendErrorEmail = async (message: any): Promise<boolean | undefined> => wrapper(async () => {
    return await API.post(UserAPI, '/error', { body: JSON.stringify(message) })
})

export type FrontPageKPI = { capital: string, projects: string, payments: string, investors: string }
export const getKPI = async (): Promise<FrontPageKPI> => wrapper(async () => {
    return await API.get(UserAPI, '/frontpagekpi/dkk', {})
})

export const getPublicProjectData = async (): Promise<PublicProjectData | undefined> => wrapper(async () => {
    return await API.get(UserAPI, '/statistic', {})
})


export const MarkNotificationAsViewed = async (notificationId: string): Promise<UINotification | undefined> => wrapper(async () => {
    setLocalItem(NOTIFICATIONS_ALIAS, undefined)
    return await API.post(UserAPI, '/notification/marked/' + notificationId, {})
})